import React, { useState, useEffect } from 'react';
import { keyBy, omit } from 'lodash';

import firebase, { httpsCallable } from '../../firebase';
import PublicPage from '../hocs/PublicPage';
import useTenant from '../hooks/useTenant';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import {
  TroubleInquiryDetailRows,
  InquiryDetailRows,
  MethodInquiryDetailRows,
  SurveyAnswerDetailRows,
} from '../InquiryDetailTable';
import {
  fields as inquiryFieldsFn,
  shipmentFields as inquiryShipmentFields,
} from '../../shared/models/inquiry';
import {
  fields as methodInquiryFieldsFn,
  shipmentFields as methodInquiryShipmentFields,
} from '../../shared/models/methodInquiry';
import { destinationFields } from '../../shared/models/order';

const db = firebase.firestore();
const getSharedInquiry = httpsCallable('getSharedInquiry');

const titles = {
  troubleInquiries: '不具合組立問合せ',
  inquiries: 'お問合せ',
  methodInquiries: '乗り方・使い方問合せ',
  surveyAnswers: 'アンケートページ回答',
};

export default PublicPage(function SharedInquiry({ match: { params: { shareKey } } }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tenant = useTenant();
  const country = tenant?.salesCountry ?? 'jp';
  const productTypes = useCollectionSubscriptionInTenant(db.collection('productTypes').orderBy('index'));
  const products = useCollectionSubscriptionInTenant(db.collection('products').orderBy('createdAt'));
  const productsById = keyBy(products, 'id');
  const inquiryTypes = useCollectionSubscriptionInTenant(db.collection('inquiryTypes'));
  const inquiryTypeChildren = useCollectionSubscriptionInTenant(db.collection('inquiryTypeChildren'));
  const surveys = useCollectionSubscriptionInTenant(db.collection('surveys'));
  const surveysById = keyBy(surveys, 'id');
  const questions = useCollectionSubscriptionInTenant(db.collection('questions'));
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getSharedInquiry({ shareKey });
        setData(data);
      } catch (e) {
        setError('データが見つかりません');
      } finally {
        setLoading(false);
      }
    })();
  }, [shareKey]);

  if (loading) return <div className="container py-5 text-center">読み込み中...</div>;
  if (error) return <div className="container py-5 text-center text-danger">{error}</div>;

  const { collectionName } = data;
  const title = titles[collectionName] || '問合せ';
  const hidePersonalInfo = !data.shareShowsPersonalInfo;

  const renderDetail = () => {
    if (collectionName === 'troubleInquiries') {
      const survey = surveysById[productsById[data.productId]?.troubleInquirySurveyId];
      const relatedQuestions = (survey?.questionRows?.map(_ => _.questionId) || []).map(_ => questions.find(q => q.id === _)).filter(_ => _);
      return (
        <TroubleInquiryDetailRows
          inquiry={data}
          productsById={productsById}
          productTypes={productTypes}
          products={products}
          country={country}
          relatedQuestions={relatedQuestions}
          hidePersonalInfo={hidePersonalInfo}
        />
      );
    }
    if (collectionName === 'inquiries') {
      const fields = {
        ...omit(inquiryFieldsFn({ country, inquiryTypes, inquiryTypeChildren }), ['files']),
        ...destinationFields({ country }),
        ...inquiryShipmentFields(),
      };
      return (
        <InquiryDetailRows
          inquiry={data}
          productsById={productsById}
          fields={fields}
          country={country}
          hidePersonalInfo={hidePersonalInfo}
        />
      );
    }
    if (collectionName === 'methodInquiries') {
      const fields = { ...omit(methodInquiryFieldsFn({ productTypes, products }), ['destinationType']), ...methodInquiryShipmentFields() };
      const survey = surveysById[productsById[data.productId]?.methodInquirySurveyId];
      const relatedQuestions = (survey?.questionRows?.map(_ => _.questionId) || []).map(_ => questions.find(q => q.id === _)).filter(_ => _);
      return (
        <MethodInquiryDetailRows
          inquiry={data}
          productsById={productsById}
          productTypes={productTypes}
          products={products}
          fields={fields}
          relatedQuestions={relatedQuestions}
          hidePersonalInfo={hidePersonalInfo}
        />
      );
    }
    if (collectionName === 'surveyAnswers') {
      const survey = surveysById[data.surveyId];
      const relatedQuestions = (survey?.questionRows?.map(_ => _.questionId) || []).map(_ => questions.find(q => q.id === _)).filter(_ => _);
      return (
        <SurveyAnswerDetailRows
          inquiry={data}
          survey={survey}
          relatedQuestions={relatedQuestions}
          hidePersonalInfo={hidePersonalInfo}
        />
      );
    }
    return null;
  };

  return (
    <div className="container py-4">
      <div className="p-4 bg-white">
        <div className="d-flex justify-content-center mb-3">
          <h4>{title}詳細</h4>
        </div>
        <table className="table table-bordered">
          <tbody className="thead-light">
            {renderDetail()}
          </tbody>
        </table>
      </div>
    </div>
  );
});
